import { useState, useEffect, Suspense } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useProfile } from '@/hooks/useProfile';
import { usePosts } from '@/hooks/usePosts';
import { useCasts } from '@/hooks/useCasts';
import { useCropPlan } from '@/hooks/useCropPlan';
import { useProfileTabs } from '@/hooks/useProfileTabs';
import { useLiteMode } from '@/hooks/useLiteMode';
import { LoadingScreen } from './LoadingScreen';
import { ErrorScreen } from './ErrorScreen';
import { ProfileEditForm } from './profile/ProfileEditForm';
import { ProfileViewContent } from './profile/ProfileViewContent';
import { ProfileHeader } from './ProfileHeader';
import { FarmProfile } from './FarmProfile';
import { JoinedCommunities } from './profile/JoinedCommunities';
import { ProfileGrantsSection } from './grants/ProfileGrantsSection';
import { ProfileViewToggle } from './profile/ProfileViewToggle';
import { ProfileSkeleton } from './skeleton/ProfileSkeleton';
import { useProfileHandlers } from './profile/useProfileHandlers';
import { useVerificationStatus } from '@/hooks/useVerificationStatus';
import { VerificationBadge } from './VerificationBadge';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

type ViewMode = 'private' | 'public';

interface ProfileProps {
  initialEditMode?: boolean;
  onEditModeUsed?: () => void;
}

export const Profile = ({ initialEditMode = false, onEditModeUsed }: ProfileProps) => {
  const navigate = useNavigate();
  const { profile, loading: profileLoading, error, refetch } = useProfile();
  const { posts, loading: postsLoading, deletePost, updatePost } = usePosts();
  const { isLiteMode } = useLiteMode();
  const { casts, loading: castsLoading, toggleSolved } = useCasts();
  const { userCropPlans, removeCropFromPlan, loading: cropLoading } = useCropPlan();
  const { isVerified, loading: verificationLoading } = useVerificationStatus();
  const {
    activeTab,
    setActiveTab,
    selectedCropForTimelapse,
    setSelectedCropForTimelapse,
    selectedPostForPopup,
    setSelectedPostForPopup,
  } = useProfileTabs();

  const [isEditing, setIsEditing] = useState(initialEditMode);
  const [editedProfile, setEditedProfile] = useState(profile);
  const [viewMode, setViewMode] = useState<ViewMode>('private');

  // Reset the edit mode flag when it's used
  useEffect(() => {
    if (initialEditMode && onEditModeUsed) {
      onEditModeUsed();
    }
  }, [initialEditMode, onEditModeUsed]);

  const loading = profileLoading || postsLoading || castsLoading || cropLoading || verificationLoading;

  // Debug logging to see if component is rendering
  console.log('🏠 Profile component rendering');
  console.log('🔧 Profile - toggleSolved function:', !!toggleSolved);
  console.log('📊 Profile - casts:', casts.length);
  console.log('👤 Profile - profile:', !!profile);

  const {
    avatarUploading,
    handleAvatarUpload,
    handleTimelapseClick,
    handlePostClick,
    handleRemoveCrop
  } = useProfileHandlers(profile, refetch);

  useEffect(() => {
    if (profile) {
      setEditedProfile(profile);
    }
  }, [profile]);

  useEffect(() => {
    document.title = profile ? `${profile.farm_name} | Farmlink` : 'Profile | Farmlink';
  }, [profile]);

  const handleRetry = () => {
    window.location.reload();
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedProfile(profile);
  };

  const handleSaveProfile = () => {
    setIsEditing(false);
    refetch();
  };

  const onTimelapseClick = (crop: string) => {
    handleTimelapseClick(crop, setSelectedCropForTimelapse, setActiveTab);
  };

  const onPostClick = (post: any) => {
    handlePostClick(post, setSelectedPostForPopup);
  };

  const onRemoveCrop = (cropId: string, cropName: string) => {
    handleRemoveCrop(cropId, cropName, removeCropFromPlan);
  };

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-bg-gradient-from to-bg-gradient-to p-4">
      <div className="max-w-4xl mx-auto">
        <ProfileSkeleton />
      </div>
    </div>
  );
  
  // If no profile found, show auth prompt for profile management
  if (!profile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-bg-gradient-from to-bg-gradient-to flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-card/90 backdrop-blur-sm rounded-lg p-6 text-center space-y-4">
            <h2 className="text-2xl font-bold text-primary">Create Your Profile</h2>
            <p className="text-muted-foreground">
              Sign in or create an account to manage your farm profile and connect with other farmers.
            </p>
            <button
              onClick={() => {
                // Navigate to auth - this will be handled by the auth system
                window.location.href = '/';
                setTimeout(() => {
                  // Force a login wall by triggering auth
                  const event = new CustomEvent('requireAuth');
                  window.dispatchEvent(event);
                }, 100);
              }}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-medium py-2 px-4 rounded-md transition-colors"
            >
              Sign In to Create Profile
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show edit form when editing
  if (isEditing && editedProfile) {
    return (
      <ProfileEditForm
        profile={profile}
        editedProfile={editedProfile}
        setEditedProfile={setEditedProfile}
        onCancel={handleCancelEdit}
        onSave={handleSaveProfile}
        avatarUploading={avatarUploading}
        onAvatarUpload={handleAvatarUpload}
      />
    );
  }

  // Show public farm profile view
  if (viewMode === 'public') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-bg-gradient-from to-bg-gradient-to">
        <div className="max-w-4xl mx-auto p-4">
          <div className="mb-4">
            <button
              onClick={() => setViewMode('private')}
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 rounded"
            >
              ← Back to Private Profile
            </button>
          </div>
          <div className="space-y-6">
            <JoinedCommunities profileId={profile.id} isOwner={true} />
            
            <FarmProfile 
              profile={profile} 
              userCropPlansCount={userCropPlans.length}
              userCropPlans={userCropPlans}
            />
          </div>
        </div>
      </div>
    );
  }

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        toast.error("Failed to log out");
        console.error('Logout error:', error);
      } else {
        toast.success("Logged out successfully");
        navigate('/');
      }
    } catch (error) {
      toast.error("Failed to log out");
      console.error('Logout error:', error);
    }
  };

  // Show private profile view
  return (
    <div className="min-h-screen bg-gradient-to-br from-bg-gradient-from to-bg-gradient-to p-2 md:p-4">
      <div className="max-w-2xl mx-auto space-y-6">
        <ProfileHeader 
          profile={profile}
          userCropPlansCount={userCropPlans.length}
          isVerified={isVerified}
        />
        
        <ProfileViewToggle 
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          onEdit={handleEdit}
          onLogout={handleLogout}
        />
        
        <ProfileViewContent
          profile={profile}
          posts={isLiteMode ? posts.slice(0, 5) : posts.slice(0, 20)}
          casts={casts}
          userCropPlans={userCropPlans}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          selectedCropForTimelapse={selectedCropForTimelapse}
          setSelectedCropForTimelapse={setSelectedCropForTimelapse}
          selectedPostForPopup={selectedPostForPopup}
          setSelectedPostForPopup={setSelectedPostForPopup}
          onEditClick={handleEdit}
          onRemoveCrop={onRemoveCrop}
          onToggleSolved={toggleSolved}
          onEditPost={updatePost}
          onDeletePost={deletePost}
          isOwner={true}
        />
        
        {/* Legal Links Footer */}
        <div className="flex items-center justify-center gap-4 py-6 text-sm text-muted-foreground">
          <Link 
            to="/terms-of-service" 
            className="hover:text-foreground transition-colors underline"
          >
            Terms of Service
          </Link>
          <span className="text-border">•</span>
          <Link 
            to="/privacy-policy" 
            className="hover:text-foreground transition-colors underline"
          >
            Privacy Policy
          </Link>
        </div>
      </div>
    </div>
  );
};